<template>
  <div class="w-full max-w-[460px] auth-page">
    <!-- Logo -->
    <div class="flex justify-center mb-4">
      <AppLogo />
    </div>

    <!-- Verified success message -->
    <div v-if="route.query.verified" class="mb-3 text-center text-sm text-emerald-300 bg-emerald-400/[0.08] border border-emerald-400/25 rounded-lg p-3">
      {{ $t('auth.verified_success') }}
    </div>

    <!-- Card — solid surface (no glassmorphism), refined hairline border. -->
    <div class="relative overflow-hidden border border-white/[0.08] bg-[var(--surface-color)] rounded-lg">
      <!-- Gradient top accent — subtle cyan strip, kept for brand identity. -->
      <div class="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-primary to-transparent opacity-50" />

      <!-- Tabs -->
      <div class="flex border-b border-white/[0.08]">
        <button
          v-for="tab in ['login', 'signup']"
          :key="tab"
          @click="activeTab = tab"
          class="flex-1 relative py-3 text-sm font-semibold transition-colors duration-200 border-none bg-transparent cursor-pointer"
          :class="activeTab === tab ? 'text-white' : 'text-text-muted hover:text-white'"
        >
          {{ tab === 'login' ? $t('auth.login_tab') : $t('auth.signup_tab') }}
          <div
            v-if="activeTab === tab"
            class="absolute bottom-0 left-1/2 -translate-x-1/2 w-12 h-[2px] bg-primary rounded-full"
          />
        </button>
      </div>

      <div class="px-7 py-6">
        <!-- LOGIN TAB -->
        <Transition name="fade" mode="out-in">
          <!-- 2FA challenge — slot in instead of the login form once the
               password leg succeeded against a 2FA-protected account. -->
          <div v-if="activeTab === 'login' && twoFactorState" key="login-2fa">
            <div class="text-center mb-4">
              <h1 class="font-heading text-xl font-bold text-white mb-1">
                {{ $t('auth.twofa_title') || 'Two-factor authentication' }}
              </h1>
              <p class="text-text-muted text-xs">
                {{ $t('auth.twofa_subtitle') || 'Enter the 6-digit code we just emailed to' }}
                <span class="text-white">{{ twoFactorState.emailHint }}</span>
              </p>
            </div>

            <form @submit.prevent="handle2faSubmit" class="space-y-3">
              <input
                ref="twoFactorCodeEl"
                v-model="twoFactorCode"
                type="text"
                inputmode="numeric"
                pattern="[0-9]*"
                maxlength="6"
                autocomplete="one-time-code"
                class="otp-input"
                :placeholder="$t('auth.twofa_placeholder') || '••••••'"
              />

              <p v-if="twoFactorError" class="text-rose-400 bg-rose-400/[0.08] p-2 rounded-lg border border-rose-400/25 text-sm text-center">
                {{ twoFactorError }}
              </p>

              <div class="auth-submit">
                <AppButton
                  variant="primary"
                  native-type="submit"
                  :loading="twoFactorLoading"
                  :disabled="twoFactorCode.length !== 6"
                  size="lg"
                >
                  {{ twoFactorLoading
                    ? ($t('auth.twofa_verifying') || 'Verifying…')
                    : ($t('auth.twofa_verify') || 'Verify code') }}
                </AppButton>
              </div>

              <div class="flex justify-between text-xs">
                <button
                  type="button"
                  class="text-text-muted hover:text-primary disabled:opacity-40 transition-colors bg-transparent border-0 cursor-pointer p-0"
                  :disabled="twoFactorResendCooldown > 0 || twoFactorResendBusy"
                  @click="handle2faResend"
                >
                  {{ twoFactorResendCooldown > 0
                    ? ($t('auth.twofa_resend_in') || 'Send a new code in') + ' ' + twoFactorResendCooldown + 's'
                    : ($t('auth.twofa_resend') || 'Send a new code') }}
                </button>
                <button
                  type="button"
                  class="text-text-muted hover:text-primary transition-colors bg-transparent border-0 cursor-pointer p-0"
                  @click="cancel2fa"
                >
                  {{ $t('auth.twofa_cancel') || 'Use a different account' }}
                </button>
              </div>
            </form>
          </div>

          <div v-else-if="activeTab === 'login'" key="login">
            <div class="text-center mb-4">
              <h1 class="font-heading text-xl font-bold text-white mb-1">{{ $t('auth.welcome_back') }}</h1>
              <p class="text-text-muted text-xs">{{ $t('auth.login_subtitle') }}</p>
            </div>

            <form @submit.prevent="handleLogin" class="space-y-3">
              <AppInput
                v-model="loginForm.email"
                :label="$t('email')"
                type="email"
                placeholder="producer@example.com"
                autocomplete="email"
                name="email"
                size="lg"
              />
              <AppInput
                v-model="loginForm.password"
                :label="$t('password')"
                type="password"
                placeholder="••••••••"
                autocomplete="current-password"
                name="password"
                size="lg"
              />

              <div class="flex justify-end -mt-1">
                <NuxtLink :to="localePath('/forgot-password')" class="text-xs text-text-muted hover:text-primary transition-colors no-underline">
                  {{ $t('auth.forgot_password') }}
                </NuxtLink>
              </div>

              <p v-if="loginError" class="text-rose-400 bg-rose-400/[0.08] p-2 rounded-lg border border-rose-400/25 text-sm text-center">{{ loginError }}</p>

              <div class="auth-submit">
                <AppButton
                  variant="primary"
                  native-type="submit"
                  :loading="loginLoading"
                  size="lg"
                >
                  {{ loginLoading ? $t('auth.logging_in') : $t('login') }}
                </AppButton>
              </div>
            </form>

            <!-- Divider — refined hairline. Hidden when no social login is active. -->
            <div v-if="anySocialLogin" class="flex items-center text-center text-text-muted my-4 text-xs before:content-[''] before:flex-1 before:border-b before:border-white/[0.08] after:content-[''] after:flex-1 after:border-b after:border-white/[0.08]">
              <span class="px-4">{{ $t('auth.or') }}</span>
            </div>

            <!-- Social Login — keep brand colors; opt-in shadow via .oauth-btn so
                 they don't read as flat per reviewer feedback. Each provider is
                 gated on the tenant's App OAuth settings (socialLogins). -->
            <div v-if="anySocialLogin" class="flex flex-col gap-2">
              <a v-if="socialLogins.spotify" :href="`/api/auth/start?provider=spotify${socialNext}`" class="oauth-btn flex items-center justify-center gap-2 w-full bg-[#1DB954] text-white font-semibold py-2.5 rounded-lg transition-all duration-200 hover:bg-[#1ed760] hover:-translate-y-px no-underline text-sm">
                <NuxtIcon name="mdi:spotify" size="20" />
                <span>{{ $t('auth.login_spotify') }}</span>
              </a>
              <a v-if="socialLogins.soundcloud" :href="`/api/auth/start?provider=soundcloud${socialNext}`" class="oauth-btn flex items-center justify-center gap-2 w-full bg-[#ff5500] text-white font-semibold py-2.5 rounded-lg transition-all duration-200 hover:bg-[#ff7700] hover:-translate-y-px no-underline text-sm">
                <NuxtIcon name="mdi:soundcloud" size="20" />
                <span>{{ $t('auth.login_soundcloud') }}</span>
              </a>
              <!-- Creator login → Facebook Login for Business (config_id),
                   creates a `creator`. The shop login uses the standard flow. -->
              <a v-if="socialLogins.facebook" :href="`/api/auth/start?provider=facebook&type=fb_business${socialNext}`" class="oauth-btn flex items-center justify-center gap-2 w-full bg-[#1877F2] text-white font-semibold py-2.5 rounded-lg transition-all duration-200 hover:bg-[#2a88ff] hover:-translate-y-px no-underline text-sm">
                <NuxtIcon name="mdi:facebook" size="20" />
                <span>{{ $t('auth.login_facebook') }}</span>
              </a>
            </div>
          </div>

          <!-- SIGNUP TAB -->
          <div v-else key="signup">
            <div class="text-center mb-4">
              <h1 class="font-heading text-xl font-bold text-white mb-1">{{ $t('auth.join_frequency') }}</h1>
              <p class="text-text-muted text-xs">{{ $t('auth.register_subtitle') }}</p>
            </div>

            <form @submit.prevent="handleRegister" class="space-y-3">
              <AppInput
                v-model="signupForm.name"
                :label="$t('auth.full_name', 'Full name')"
                :placeholder="$t('auth.full_name_placeholder', 'Your real name (first & last)')"
                autocomplete="name"
                name="name"
                size="lg"
              />
              <AppInput
                v-model="signupForm.email"
                :label="$t('email')"
                type="email"
                placeholder="producer@example.com"
                autocomplete="email"
                name="email"
                size="lg"
              />
              <AppInput
                v-model="signupForm.password"
                :label="$t('password')"
                type="password"
                placeholder="••••••••"
                autocomplete="new-password"
                name="password"
                size="lg"
              />

              <el-checkbox v-model="signupForm.termsAccepted" class="text-white">
                {{ $t('auth.i_accept') }}
                <NuxtLink :to="localePath('/terms')" target="_blank" class="text-primary hover:underline ml-1">
                  {{ $t('terms.title') }}
                </NuxtLink>
              </el-checkbox>

              <p v-if="signupError" class="text-rose-400 bg-rose-400/[0.08] p-2 rounded-lg border border-rose-400/25 text-sm text-center">{{ signupError }}</p>

              <div class="auth-submit">
                <AppButton
                  variant="primary"
                  native-type="submit"
                  :loading="signupLoading"
                  :disabled="!signupForm.termsAccepted"
                  size="lg"
                >
                  {{ signupLoading ? $t('auth.creating_account') : $t('auth.create_account') }}
                </AppButton>
              </div>
            </form>

            <!-- Divider — hidden when no social login is active. -->
            <div v-if="anySocialLogin" class="flex items-center text-center text-text-muted my-4 text-xs before:content-[''] before:flex-1 before:border-b before:border-white/[0.08] after:content-[''] after:flex-1 after:border-b after:border-white/[0.08]">
              <span class="px-4">{{ $t('auth.or') }}</span>
            </div>

            <!-- Social Signup — native <button> so we can wire @click handlers
                 that set the auth_action cookie before redirecting (the login
                 variant is plain links because spotify/soundcloud OAuth there
                 doesn't need the register flag). Gated on tenant App OAuth. -->
            <div v-if="anySocialLogin" class="flex flex-col gap-2">
              <button v-if="socialLogins.spotify" @click="handleSocialRegister('spotify')" class="oauth-btn flex items-center justify-center gap-2 w-full bg-[#1DB954] text-white font-semibold py-2.5 rounded-lg transition-all duration-200 hover:bg-[#1ed760] hover:-translate-y-px border-none cursor-pointer text-sm">
                <NuxtIcon name="mdi:spotify" size="20" />
                <span>{{ $t('auth.signup_spotify') }}</span>
              </button>
              <button v-if="socialLogins.soundcloud" @click="handleSocialRegister('soundcloud')" class="oauth-btn flex items-center justify-center gap-2 w-full bg-[#ff5500] text-white font-semibold py-2.5 rounded-lg transition-all duration-200 hover:bg-[#ff7700] hover:-translate-y-px border-none cursor-pointer text-sm">
                <NuxtIcon name="mdi:soundcloud" size="20" />
                <span>{{ $t('auth.signup_soundcloud') }}</span>
              </button>
              <button v-if="socialLogins.facebook" @click="handleSocialRegister('facebook')" class="oauth-btn flex items-center justify-center gap-2 w-full bg-[#1877F2] text-white font-semibold py-2.5 rounded-lg transition-all duration-200 hover:bg-[#2a88ff] hover:-translate-y-px border-none cursor-pointer text-sm">
                <NuxtIcon name="mdi:facebook" size="20" />
                <span>{{ $t('auth.signup_facebook') }}</span>
              </button>
            </div>
          </div>
        </Transition>
      </div>
    </div>

    <!-- Footer text -->
    <p class="mt-3 text-center text-text-muted text-xs">
      &copy; {{ new Date().getFullYear() }} {{ appName }}. All rights reserved.
    </p>
  </div>
</template>

<script setup lang="ts">
definePageMeta({ layout: 'auth' })
const { appName, socialLogins } = useTenantTheme()
// True when at least one social-login provider is active — used to hide the
// "or" divider + button block entirely on tenants with all logins off.
const anySocialLogin = computed(() =>
  !!(socialLogins.value.spotify || socialLogins.value.soundcloud || socialLogins.value.facebook)
)

const { login, verifyTwoFactor, resendTwoFactor, register, user } = useAuth()
const router = useRouter()
const route = useRoute()
const localePath = useLocalePath()
const { t: $i18n } = useI18n()
const roleCookie = useCookie('signup_role', { maxAge: 3600 })
const authAction = useCookie('auth_action', { maxAge: 300 })

// Default tab based on route query or 'login'
const activeTab = ref(route.query.tab === 'signup' ? 'signup' : 'login')

// --- Login ---
const loginForm = reactive({ email: '', password: '' })
const loginLoading = ref(false)
// Pre-populate from ?error=... so OAuth redirects (e.g. cross-tenant email
// collision from /api/auth/bridge) surface the message in the form instead
// of leaving it stranded in the URL.
const loginError = ref(typeof route.query.error === 'string' ? route.query.error : '')

// A same-origin page can send people here with ?next=<path> — the connector
// consent page (/oauth/authorize) does, so Claude or ChatGPT users land back
// on their consent screen after signing in. Honoured after login instead of
// the role-based landing; relative paths only, never a foreign host. The
// social buttons carry it through the OAuth round-trip (auth_return_url).
//
// Read on the SERVER and handed to the client through the payload: after
// hydration the browser URL is rewritten in decoded form (`next=/oauth/…?a&b`),
// which cuts the value at its first `&` — the SSR value is the intact one.
const nextState = useState('auth-next-path', () => {
  const n = route.query.next
  return typeof n === 'string' && n.startsWith('/') && !n.startsWith('//') && !n.includes('\\') ? n : ''
})
const nextPath = nextState.value
const socialNext = nextPath ? `&next=${encodeURIComponent(nextPath)}` : ''
onUnmounted(() => { nextState.value = undefined })

// Where to land the user after a successful login (or post-2FA). Pulled
// out of handleLogin so the 2FA verification path can reuse the exact
// same role-based routing without duplicating the branching.
function redirectAfterLogin() {
  if (nextPath) {
    window.location.href = nextPath
    return
  }
  const u = user.value
  const role = u?.role
  const isLabelManager =
      !u?.parentCreator
      && (role === 'label_manager'
          || (role === 'creator' && u?.canCreateLabels === true))

  if (isLabelManager) {
    window.location.href = getSubdomainUrl('labels', '/')
  } else if (role === 'ar') {
    window.location.href = getSubdomainUrl('scouts', '/')
  } else if (role === 'creator' || role === 'label_manager') {
    window.location.href = getSubdomainUrl('creator', '/')
  } else if (role === 'admin') {
    window.location.href = getSubdomainUrl('admin', '/')
  } else {
    router.push(localePath('/'))
  }
}

// 2FA challenge state. Populated when the server returns
// `twoFactorRequired:true`; cleared on success or cancel.
const twoFactorState = ref(null)
const twoFactorCode = ref('')
const twoFactorError = ref('')
const twoFactorLoading = ref(false)
const twoFactorResendBusy = ref(false)
const twoFactorResendCooldown = ref(0)
const twoFactorCodeEl = ref(null)
let twoFactorResendTimer = null

function startTwoFactorCooldown(seconds) {
  twoFactorResendCooldown.value = Math.max(0, seconds | 0)
  if (twoFactorResendTimer) {
    clearInterval(twoFactorResendTimer)
    twoFactorResendTimer = null
  }
  if (twoFactorResendCooldown.value <= 0) return
  twoFactorResendTimer = setInterval(() => {
    twoFactorResendCooldown.value--
    if (twoFactorResendCooldown.value <= 0) {
      clearInterval(twoFactorResendTimer)
      twoFactorResendTimer = null
    }
  }, 1000)
}

onBeforeUnmount(() => {
  if (twoFactorResendTimer) clearInterval(twoFactorResendTimer)
})

// Detect a pending 2FA challenge stashed in the signed `pending_2fa`
// cookie by the OAuth bridge. When present, we skip the password
// form entirely and drop straight into the OTP entry UI. The cookie
// is HttpOnly so we round-trip via /api/auth/pending-2fa to read it.
//
// We deliberately use `$fetch` inside `onMounted` rather than
// `useFetch` at top-level. The reason: useFetch caches its data ref
// by auto-generated key across client-side navigations. A user who
// successfully completed the OTP, navigated to /admin, then hit
// Back to /auth would otherwise see the CACHED `{pending:true}`
// payload — even though the underlying cookie was server-cleared on
// success — and would be presented with a stale, already-consumed
// challengeId. Fetching on every mount via $fetch sidesteps that.
// The cost is a flash of the password form on first paint for
// OAuth-2FA users, which is acceptable given how rare the path is.
const pending2faPending = ref(false)
const pending2faPayload = ref<any>(null)

async function loadPending2fa() {
  try {
    const res: any = await $fetch('/api/auth/pending-2fa')
    pending2faPending.value = res?.pending === true
    pending2faPayload.value = res
  } catch {
    pending2faPending.value = false
    pending2faPayload.value = null
  }
}

onMounted(async () => {
  await loadPending2fa()
  if (pending2faPending.value && pending2faPayload.value && !twoFactorState.value) {
    twoFactorState.value = {
      challengeId: pending2faPayload.value.challengeId,
      expiresAt: pending2faPayload.value.expiresAt,
      emailHint: pending2faPayload.value.emailHint || '',
      origin: pending2faPayload.value.origin,
      provider: pending2faPayload.value.provider || null
    }
    activeTab.value = 'login'
    startTwoFactorCooldown(30)
    await nextTick()
  }
  if (twoFactorState.value && !twoFactorCode.value) {
    twoFactorCodeEl.value?.focus?.()
  }
})

const handleLogin = async () => {
  loginLoading.value = true
  loginError.value = ''

  const res = await login(loginForm.email, loginForm.password)

  if (res.success) {
    redirectAfterLogin()
  } else if (res.twoFactorRequired) {
    // Switch the card into 2FA mode. Don't clear loginForm — if the
    // user cancels the 2FA step they should land back on a pre-filled
    // login form so they don't retype the email.
    twoFactorState.value = {
      challengeId: res.challengeId,
      expiresAt: res.expiresAt,
      emailHint: res.emailHint || ''
    }
    twoFactorCode.value = ''
    twoFactorError.value = ''
    startTwoFactorCooldown(30)
    await nextTick()
    twoFactorCodeEl.value?.focus?.()
  } else {
    loginError.value = res.error
  }
  loginLoading.value = false
}

const handle2faSubmit = async () => {
  if (!twoFactorState.value) return
  twoFactorLoading.value = true
  twoFactorError.value = ''
  const res = await verifyTwoFactor(twoFactorState.value.challengeId, twoFactorCode.value)
  if (res.success) {
    // OAuth-initiated logins arrive here with a server-validated
    // returnUrl (the `next` from the OAuth flow, vetted at issue
    // time). Mobile flows return the diztree:// deep link.
    if (res.redirectUrl) {
      if (res.redirectUrl.startsWith('diztree://')) {
        // Native deep link — full navigation hands control to the OS,
        // which routes back into the installed app.
        window.location.href = res.redirectUrl
      } else if (res.redirectUrl.startsWith('http://') || res.redirectUrl.startsWith('https://')) {
        // Fully-qualified URL (cross-subdomain hop). The server
        // already vetted it via safePendingNext.
        window.location.href = res.redirectUrl
      } else {
        // Same-origin relative path — use the router so client-side
        // navigation kicks in.
        router.push(res.redirectUrl)
      }
    } else {
      redirectAfterLogin()
    }
  } else {
    twoFactorError.value = res.error || 'Verification failed'
    if (typeof res.attemptsLeft === 'number' && res.attemptsLeft > 0) {
      twoFactorError.value += ` (${res.attemptsLeft} ${$i18n('auth.twofa_attempts_left') || 'attempts left'})`
    }
    // Hard reasons (expired / consumed / not_found / too_many_attempts)
    // burn the challenge — fall back to the password form so the user
    // can request a fresh code via a normal login.
    const dead = ['expired', 'consumed', 'not_found', 'too_many_attempts']
    if (res.reason && dead.includes(res.reason)) {
      // Keep the message visible but reset the input so the user can't
      // resubmit the dead code.
      twoFactorCode.value = ''
    }
  }
  twoFactorLoading.value = false
}

const handle2faResend = async () => {
  if (!twoFactorState.value || twoFactorResendCooldown.value > 0) return
  twoFactorResendBusy.value = true
  twoFactorError.value = ''
  const res = await resendTwoFactor(twoFactorState.value.challengeId)
  if (res.success) {
    twoFactorState.value = {
      ...twoFactorState.value,
      challengeId: res.challengeId,
      expiresAt: res.expiresAt
    }
    startTwoFactorCooldown(30)
  } else {
    twoFactorError.value = res.error || 'Could not resend code'
  }
  twoFactorResendBusy.value = false
}

async function cancel2fa() {
  twoFactorState.value = null
  twoFactorCode.value = ''
  twoFactorError.value = ''
  if (twoFactorResendTimer) {
    clearInterval(twoFactorResendTimer)
    twoFactorResendTimer = null
  }
  twoFactorResendCooldown.value = 0
  // Clear the OAuth-bridge pending cookie too — without this, an
  // OAuth-initiated 2FA prompt that the user backed out of would
  // bounce them straight back into the OTP UI on their next /auth
  // visit (the cookie persists for ~11 min by default).
  try {
    await $fetch('/api/auth/pending-2fa', { method: 'DELETE' })
  } catch {
    // Network failure on cancel is non-fatal — the cookie expires
    // by itself in ~11 minutes regardless.
  }
}

// --- Signup ---
const signupForm = reactive({
  name: '',
  email: '',
  password: '',
  role: 'creator',
  termsAccepted: false
})
const signupLoading = ref(false)
const signupError = ref('')

roleCookie.value = 'creator'

const handleRegister = async () => {
  signupLoading.value = true
  signupError.value = ''

  const res = await register(signupForm.name, signupForm.email, signupForm.password, signupForm.role, signupForm.termsAccepted)

  if (res.success) {
    // Freemium signup: straight into the dashboard on the Creator-Free tier —
    // no plan step, no Stripe redirect, no forced artist wizard. The dashboard
    // prompts for the first artist and the account page offers the upgrade to
    // a paid tier. Same target for social signup (see /register/finish).
    window.location.href = getSubdomainUrl('creator', '/')
  } else {
    signupError.value = res.error
  }
  signupLoading.value = false
}

const handleSocialRegister = (provider) => {
  authAction.value = 'register'
  roleCookie.value = 'creator'
  // Facebook on the creator page → business flow (config_id + creator role).
  const typeParam = provider === 'facebook' ? '&type=fb_business' : ''
  window.location.href = `/api/auth/start?provider=${provider}&auth_action=register${typeParam}${socialNext}`
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* AppButton wraps el-button which renders inline-flex by default —
   stretching the submit CTA to fill the form width needs a width
   handle on the inner el-button (the Tailwind `!w-full` class on the
   AppButton wrapper would only affect the wrapper div). */
.auth-submit :deep(.el-button) {
  width: 100%;
}

/* Subtle elevation on OAuth buttons. Reviewer flagged that stripping the
   keyed shadow left them feeling flat — the refined-look bans heavy glow,
   but a soft 4-12px shadow is fine and reads as a tactile "button". The
   shadow strengthens on hover so the press affordance is preserved. */
.oauth-btn {
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
}
.oauth-btn:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.35);
}

/* OTP input — large monospaced field used in the 2FA challenge step.
   Wider letter-spacing makes the 6 digits feel grouped without forcing
   per-digit input boxes (which would be cleaner but add a fair bit of
   complexity for a feature most users hit < once a week). */
.otp-input {
  width: 100%;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  color: white;
  font-family: 'SF Mono', SFMono-Regular, ui-monospace, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
  font-size: 26px;
  font-weight: 700;
  letter-spacing: 0.4em;
  text-align: center;
  padding: 14px 16px;
  outline: 0;
  transition: border-color 0.15s ease, background 0.15s ease;
}
.otp-input:focus {
  border-color: rgb(var(--primary-channels) / 0.6);
  background: rgb(var(--primary-channels) / 0.06);
}
.otp-input::placeholder {
  color: rgba(255, 255, 255, 0.2);
  letter-spacing: 0.4em;
}
</style>
